import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Table,
  Space,
  Button,
  Tag,
  Select,
  DatePicker,
  Input,
  Row,
  Col,
  message,
  Modal,
  Form,
  Tooltip,
  Drawer,
  Descriptions,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  FileExcelOutlined,
  HistoryOutlined,
  CheckOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { invoiceService } from '../../services';
import { formatCurrency, formatDate, debounce, getUserInfo } from '../../utils/helpers';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const InvoiceExportList = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({
    statusId: null,
    dateFrom: null,
    dateTo: null,
    keyword: '',
  });
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [statusHistoryVisible, setStatusHistoryVisible] = useState(false);
  const [statusHistory, setStatusHistory] = useState([]);
  const [updateStatusVisible, setUpdateStatusVisible] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const isStock = getUserInfo()?.role === 'stock';

  // Danh sách trạng thái hóa đơn từ API
  const [invoiceStatuses, setInvoiceStatuses] = useState([]);

  const getStatusInfo = (statusId) => {
    const status = invoiceStatuses.find(s => s.id === statusId);
    if (status) {
      // Mapping màu sắc dựa trên tên trạng thái
      let color = 'default';
      switch (status.id) {
        case 1: color = 'processing'; break; // Chờ Xác Nhận
        case 2: color = 'blue'; break;       // Đơn Đang Chuẩn Bị
        case 3: color = 'warning'; break;    // Đã gửi cho đơn vị vận chuyển
        case 4: color = 'success'; break;    // Đã Giao Hàng
        case 5: color = 'error'; break;      // Đơn hàng đã bị hủy
        case 6: color = 'green'; break;      // Đã Hoàn Thành
        default: color = 'default';
      }
      return { name: status.name, color: color };
    }
    return { name: 'Không xác định', color: 'default' };
  };

  useEffect(() => {
    loadInvoices();
    loadInvoiceStatuses();

    // Mobile detection
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [pagination.current, pagination.pageSize, filters]);

  const loadInvoiceStatuses = async () => {
    try {
      const response = await invoiceService.export.getStatuses();
      
      if (response.success) {
        setInvoiceStatuses(response.data);
      } else {
        message.error('Không thể tải danh sách trạng thái hóa đơn');
      }
    } catch (error) {
      //console.error('Error loading invoice statuses:', error);
      message.error('Lỗi khi tải danh sách trạng thái hóa đơn');
    }
  };

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const params = {
        offset: (pagination.current - 1) * pagination.pageSize,
        limit: pagination.pageSize,
        ...(filters.statusId && { statusId: filters.statusId }),
        ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
        ...(filters.dateTo && { dateTo: filters.dateTo }),
        ...(filters.keyword && { keyword: filters.keyword }),
      };

      const response = await invoiceService.export.getAll(params);
      //console.log('API Response:', response); // Debug log
      
      if (response.success) {
        setInvoices(response.data || []);
        
        // Kiểm tra cấu trúc response để lấy total
        const dataArray = response.data || [];
        const total = response.total || response.pagination?.total || response.count || 0;
        
        // Fallback: Nếu API không trả về total và số record = pageSize, giả định còn trang sau
        const estimatedTotal = total > 0 ? total : (dataArray.length === pagination.pageSize ? pagination.current * pagination.pageSize + 1 : pagination.current * pagination.pageSize);
        
        //console.log('Total records:', total, 'Estimated total:', estimatedTotal); // Debug log
        
        setPagination(prev => ({
          ...prev,
          total: estimatedTotal,
        }));
      } else {
        message.error(response.message || 'Không thể tải danh sách hóa đơn');
      }
    } catch (error) {
      //console.error('Error loading invoices:', error);
      message.error('Có lỗi xảy ra khi tải danh sách hóa đơn');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = debounce((value) => {
    setFilters(prev => ({ ...prev, keyword: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  }, 500);

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setFilters(prev => ({
        ...prev,
        dateFrom: dates[0].format('YYYY-MM-DD'),
        dateTo: dates[1].format('YYYY-MM-DD'),
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        dateFrom: null,
        dateTo: null,
      }));
    }
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleStatusFilter = (statusId) => {
    setFilters(prev => ({ ...prev, statusId }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const buildShippingAddress = (invoice) => {
    if (!invoice) return '';

    return [
      invoice.shipping_house_number,
      invoice.shipping_street,
      invoice.shipping_address_detail,
      invoice.shipping_ward,
      invoice.shipping_district,
      invoice.shipping_province
    ]
      .filter(Boolean)
      .join(', ')
      .replace(/Bỏ qua\s*-\s*Sử dụng địa chỉ 2 cấp/gi, '')
      .replace(/\s*,\s*,+/g, ', ')
      .replace(/^\s*,\s*|\s*,\s*$/g, '')
      .trim();
  };

  const handleCopyShippingAddress = async () => {
    const address = buildShippingAddress(selectedInvoice);

    if (!address) {
      message.warning('Không có địa chỉ giao hàng để copy');
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(address);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = address;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      message.success('Đã copy địa chỉ giao hàng');
    } catch (error) {
      message.error('Không thể copy địa chỉ giao hàng');
    }
  };

  // Mobile Invoice Card Component
  const MobileInvoiceCard = ({ invoice }) => {
    const statusInfo = getStatusInfo(invoice.status_id);
    const isCompleted = invoice.status_id === 4 || invoice.status_id === 5;

    return (
      <Card 
        size="small" 
        style={{ 
          marginBottom: 12,
          borderLeft: `4px solid ${statusInfo.color === 'success' ? '#52c41a' : 
                     statusInfo.color === 'error' ? '#ff4d4f' : 
                     statusInfo.color === 'processing' ? '#1890ff' : '#faad14'}`
        }}
      >
        <div style={{ marginBottom: 8 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Button 
                type="link" 
                style={{ padding: 0, fontSize: '14px', fontWeight: 'bold' }}
                onClick={() => handleViewDetail(invoice)}
              >
                {invoice.code || `#${invoice.id}`}
              </Button>
            </Col>
            <Col>
              <Tag color={statusInfo.color} style={{ margin: 0 }}>
                {statusInfo.name}
              </Tag>
            </Col>
          </Row>
          <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
            📅 {formatDate(invoice.created_at)}
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
            👤 {invoice.customer_name}
          </div>
          <div style={{ fontSize: 12, marginBottom: 4 }}>
            <Tag color={invoice.order_type === 'Đơn mới' ? 'blue' : 'orange'} style={{ fontSize: 11 }}>
              {invoice.order_type}
            </Tag>
          </div>
        </div>

        <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
          <Col>
            <div style={{ fontSize: 11, color: '#666' }}>Tổng tiền:</div>
            <div style={{ fontWeight: 'bold', color: '#1890ff', fontSize: 16 }}>
              {formatCurrency(invoice.total_amount)}
            </div>
          </Col>
          <Col>
            <Space size="small">
              <Button
                type="text"
                icon={<EyeOutlined />}
                size="small"
                onClick={() => handleViewDetail(invoice)}
                title="Xem chi tiết"
              />
              <Button
                type="text"
                icon={<HistoryOutlined />}
                size="small"
                onClick={() => handleViewStatusHistory(invoice)}
                title="Lịch sử"
              />
              {!isCompleted && (
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() => handleUpdateStatus(invoice)}
                  title="Cập nhật trạng thái"
                />
              )}
            </Space>
          </Col>
        </Row>
      </Card>
    );
  };

  const handleTableChange = (newPagination) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  const handleViewDetail = async (invoice) => {
    try {
      const response = await invoiceService.export.getById(invoice.id);
      if (response.success) {
        // Gộp thông tin invoice và details thành 1 object cho Modal
        const invoiceDetail = {
          ...response.data.invoice,
          details: response.data.details
        };
        setSelectedInvoice(invoiceDetail);
        setDetailVisible(true);
      } else {
        message.error('Không thể tải chi tiết hóa đơn');
      }
    } catch (error) {
      //console.error('Error loading invoice detail:', error);
      message.error('Có lỗi xảy ra khi tải chi tiết hóa đơn');
    }
  };

  const handleViewStatusHistory = async (invoice) => {
    try {
      const response = await invoiceService.export.getStatusHistory(invoice.id);
      if (response.success) {
        setStatusHistory(response.data || []);
        setSelectedInvoice(invoice);
        setStatusHistoryVisible(true);
      } else {
        message.error('Không thể tải lịch sử trạng thái');
      }
    } catch (error) {
      //console.error('Error loading status history:', error);
      message.error('Có lỗi xảy ra khi tải lịch sử trạng thái');
    }
  };

  const handleUpdateStatus = (invoice) => {
    setSelectedInvoice(invoice);
    form.setFieldsValue({
      statusId: invoice.status_id,
      note: '',
    });
    setUpdateStatusVisible(true);
  };

  const handleStatusUpdate = async (values) => {
    try {
      const response = await invoiceService.export.updateStatus(selectedInvoice.id, {
        statusId: values.statusId,
        note: values.note,
      });

      if (response.success) {
        message.success('Cập nhật trạng thái thành công');
        setUpdateStatusVisible(false);
        loadInvoices();
      } else {
        message.error(response.message || 'Cập nhật trạng thái thất bại');
      }
    } catch (error) {
      //console.error('Error updating status:', error);
      message.error('Có lỗi xảy ra khi cập nhật trạng thái');
    }
  };

  const columns = [
    {
      title: 'Mã hóa đơn',
      dataIndex: 'code',
      key: 'code',
      width: 150,
      render: (code, record) => (
        <Space direction="vertical" size={0}>
          <Button 
            type="link" 
            style={{ padding: 0, fontSize: '14px', fontWeight: 'bold' }}
            onClick={() => handleViewDetail(record)}
          >
            {code || `#${record.id}`}
          </Button>
          <small style={{ color: '#999' }}>{formatDate(record.created_at)}</small>
        </Space>
      ),
    },
    {
      title: 'Khách hàng',
      dataIndex: 'customer_name',
      key: 'customer_name',
      width: 200,
    },
    {
      title: 'Loại đơn',
      dataIndex: 'order_type',
      key: 'order_type',
      width: 100,
      render: (text) => (
        <Tag color={text === 'Đơn mới' ? 'blue' : 'orange'}>{text}</Tag>
      ),
    },
    {
      title: 'Tổng tiền',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 120,
      align: 'right',
      render: (amount) => (
        <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
          {formatCurrency(amount)}
        </div>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status_id',
      key: 'status_id',
      width: 120,
      render: (statusId) => {
        const status = getStatusInfo(statusId);
        return <Tag color={status.color}>{status.name}</Tag>;
      },
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Xem chi tiết">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleViewDetail(record)}
            />
          </Tooltip>
          <Tooltip title="Lịch sử trạng thái">
            <Button
              type="text"
              icon={<HistoryOutlined />}
              size="small"
              onClick={() => handleViewStatusHistory(record)}
            />
          </Tooltip>
          <Tooltip title="Cập nhật trạng thái">
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleUpdateStatus(record)}
              disabled={record.status_id === 4}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row justify="space-between" align="middle">
            <Col xs={24} sm={12}>
              <Title level={3} style={{ margin: 0 }}>
                Danh sách hóa đơn xuất
              </Title>
            </Col>
            <Col xs={24} sm={12} style={{ textAlign: isMobile ? 'left' : 'right', marginTop: isMobile ? 12 : 0 }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/invoices/export/add')}
                block={isMobile}
              >
                Tạo hóa đơn
              </Button>
            </Col>
          </Row>
        </div>

        {/* Filters */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Input
              placeholder="Tìm kiếm theo mã, tên khách hàng..."
              prefix={<SearchOutlined />}
              allowClear
              onChange={(e) => handleSearch(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              placeholder="Lọc theo trạng thái"
              style={{ width: '100%' }}
              allowClear
              onChange={handleStatusFilter}
            >
              {invoiceStatuses.map(status => (
                <Option key={status.id} value={status.id}>
                  <Tag color={status.color}>{status.name}</Tag>
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={24} md={8} lg={6}>
            <RangePicker
              style={{ width: '100%' }}
              placeholder={['Từ ngày', 'Đến ngày']}
              onChange={handleDateRangeChange}
            />
          </Col>
        </Row>

        {isMobile ? (
          // Mobile: Card Layout
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {invoices.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: 40,
                color: '#999'
              }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
                <div>Không có hóa đơn nào</div>
              </div>
            ) : (
              <>
                <div style={{ 
                  marginBottom: 12,
                  padding: '8px 12px',
                  backgroundColor: '#f0f9ff',
                  borderRadius: 6,
                  fontSize: 12,
                  color: '#1890ff'
                }}>
                  📱 Hiển thị {invoices.length} hóa đơn
                </div>
                {invoices.map((invoice) => (
                  <MobileInvoiceCard key={invoice.id} invoice={invoice} />
                ))}
                {pagination.total > invoices.length && (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: 16,
                    borderTop: '1px solid #f0f0f0'
                  }}>
                    <Button 
                      onClick={() => setPagination(prev => ({ 
                        ...prev, 
                        current: prev.current + 1 
                      }))}
                      loading={loading}
                    >
                      Tải thêm ({pagination.total - invoices.length} còn lại)
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          // Desktop: Table Layout
          <Table
            columns={columns}
            dataSource={invoices}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} hóa đơn`,
            }}
            onChange={handleTableChange}
            scroll={{ x: 950 }}
          />
        )}
      </Card>

      {/* Chi tiết hóa đơn */}
      <Drawer
        title="Chi tiết hóa đơn"
        placement="right"
        size={isMobile ? "default" : "large"}
        width={isMobile ? "100%" : undefined}
        onClose={() => setDetailVisible(false)}
        open={detailVisible}
      >
        {selectedInvoice && (
          <div>
            {isMobile ? (
              // Mobile: Card Layout
              <div>
                {!isStock && (
                <>
                <Card size="small" title="Thông tin hóa đơn" style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: '#666' }}>Mã hóa đơn:</div>
                    <div style={{ fontWeight: 'bold' }}>
                      {selectedInvoice.code || `#${selectedInvoice.id}`}
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: '#666' }}>Ngày tạo:</div>
                    <div>{formatDate(selectedInvoice.created_at)}</div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: '#666' }}>Trạng thái:</div>
                    <Tag color={getStatusInfo(selectedInvoice.status_id).color}>
                      {getStatusInfo(selectedInvoice.status_id).name}
                    </Tag>
                  </div>
                </Card>

                <Card size="small" title="Thông tin khách hàng" style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: '#666' }}>Tên khách hàng:</div>
                    <div style={{ fontWeight: 'bold' }}>
                      {selectedInvoice.customer_name || selectedInvoice.shipping_receiver_name}
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: '#666' }}>Số điện thoại:</div>
                    <div>{selectedInvoice.shipping_phone || selectedInvoice.common_phone}</div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: '#666' }}>Email:</div>
                    <div>{selectedInvoice.email || 'Không có'}</div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: '#666' }}>CCCD:</div>
                    <div>{selectedInvoice.cccd || 'Không có'}</div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: '#666' }}>Số điểm sử dụng:</div>
                    <div>{selectedInvoice.SoDiemSuDung || '0'}</div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      <div style={{ fontSize: 12, color: '#666' }}>Địa chỉ giao hàng:</div>
                      <Tooltip title="Copy địa chỉ giao hàng">
                        <Button
                          type="text"
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={handleCopyShippingAddress}
                        />
                      </Tooltip>
                    </div>
                    <div style={{ fontSize: 13, lineHeight: 1.4 }}>
                      {buildShippingAddress(selectedInvoice) || 'Không có'}
                    </div>
                  </div>
                </Card>

                <Card size="small" title="Thông tin thanh toán" style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#666' }}>Tạm tính:</span>
                      <span>{formatCurrency(selectedInvoice.sub_total || 0)}</span>
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#666' }}>Phí giao hàng:</span>
                      <span>{formatCurrency(selectedInvoice.shipping_fee || 0)}</span>
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#666' }}>Giảm giá:</span>
                      <span>{formatCurrency(selectedInvoice.discount_amount || 0)}</span>
                    </div>
                  </div>
                  <div style={{ paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 'bold' }}>Tổng tiền:</span>
                      <span style={{ fontWeight: 'bold', color: '#1890ff', fontSize: 18 }}>
                        {formatCurrency(selectedInvoice.total_amount)}
                      </span>
                    </div>
                  </div>
                </Card>

                {selectedInvoice.note && (
                  <Card size="small" title="Ghi chú" style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 13, lineHeight: 1.4 }}>
                      {selectedInvoice.note}
                    </div>
                  </Card>
                )}
                </>
                )}

                {selectedInvoice.details && selectedInvoice.details.length > 0 && (
                  <Card size="small" title={`Chi tiết sản phẩm (${selectedInvoice.details.length})`}>
                    {selectedInvoice.details.map((item, index) => (
                      <Card 
                        key={item.variant_id || index} 
                        size="small" 
                        style={{ marginBottom: 12, backgroundColor: '#fafafa' }}
                      >
                        <div style={{ display: 'flex', gap: 12 }}>
                          <img 
                            src={item.variant_image} 
                            alt={item.product_name}
                            style={{ 
                              width: 50, 
                              height: 50, 
                              objectFit: 'cover',
                              borderRadius: 4,
                              flexShrink: 0
                            }}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 'bold', marginBottom: 4, fontSize: 13 }}>
                              {item.product_name}
                            </div>
                            <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>
                              SKU: {item.variant_sku}
                            </div>
                            {item.variant_attributes && (
                              <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>
                                {item.variant_attributes}
                              </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: 12 }}>
                                SL: <strong>{item.quantity}</strong>
                              </span>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: 11, color: '#666' }}>
                                  {formatCurrency(item.price)} x {item.quantity}
                                </div>
                                <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
                                  {formatCurrency(item.amount)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </Card>
                )}
              </div>
            ) : (
              // Desktop: Original Layout
              <div>
                {!isStock && (
                <Descriptions title="Thông tin hóa đơn" column={2} bordered>
                  <Descriptions.Item label="Mã hóa đơn">
                    {selectedInvoice.code || `#${selectedInvoice.id}`}
                  </Descriptions.Item>
                  <Descriptions.Item label="Ngày tạo">
                    {formatDate(selectedInvoice.created_at)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Khách hàng">
                    {selectedInvoice.customer_name || selectedInvoice.shipping_receiver_name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {selectedInvoice.email || 'Không có'}
                  </Descriptions.Item>
                  <Descriptions.Item label="CCCD">
                    {selectedInvoice.cccd || 'Không có'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Số điểm sử dụng">
                    {selectedInvoice.SoDiemSuDung || '0'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Số điện thoại">
                    {selectedInvoice.shipping_phone || selectedInvoice.common_phone}
                  </Descriptions.Item>
                  <Descriptions.Item label="Địa chỉ giao hàng" span={2}>
                    <Space>
                      <span>{buildShippingAddress(selectedInvoice) || 'Không có'}</span>
                      <Tooltip title="Copy địa chỉ giao hàng">
                        <Button
                          type="text"
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={handleCopyShippingAddress}
                        />
                      </Tooltip>
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label="Trạng thái">
                    <Tag color={getStatusInfo(selectedInvoice.status_id).color}>
                      {getStatusInfo(selectedInvoice.status_id).name}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Phí giao hàng">
                    {formatCurrency(selectedInvoice.shipping_fee || 0)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Tạm tính">
                    {formatCurrency(selectedInvoice.sub_total || 0)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Giảm giá">
                    {formatCurrency(selectedInvoice.discount_amount || 0)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Tổng tiền">
                    <div style={{ fontWeight: 'bold', color: '#1890ff', fontSize: '16px' }}>
                      {formatCurrency(selectedInvoice.total_amount)}
                    </div>
                  </Descriptions.Item>
                  {selectedInvoice.note && (
                    <Descriptions.Item label="Ghi chú" span={2}>
                      {selectedInvoice.note}
                    </Descriptions.Item>
                  )}
                </Descriptions>
                )}

                {selectedInvoice.details && selectedInvoice.details.length > 0 && (
                  <>
                    <Divider />
                    <Title level={5}>Chi tiết sản phẩm</Title>
                    <Table
                      dataSource={selectedInvoice.details}
                      rowKey="variant_id"
                      pagination={false}
                      size="small"
                      columns={[
                        {
                          title: 'Hình ảnh',
                          dataIndex: 'variant_image',
                          key: 'variant_image',
                          width: 60,
                          render: (image) => (
                            <img 
                              src={image} 
                              alt="Product" 
                              style={{ width: 40, height: 40, objectFit: 'cover' }}
                            />
                          ),
                        },
                        {
                          title: 'Sản phẩm',
                          dataIndex: 'product_name',
                          key: 'product_name',
                        },
                        {
                          title: 'SKU',
                          dataIndex: 'variant_sku',
                          key: 'variant_sku',
                        },
                        {
                          title: 'Thuộc tính',
                          dataIndex: 'variant_attributes',
                          key: 'variant_attributes',
                        },
                        {
                          title: 'Số lượng',
                          dataIndex: 'quantity',
                          key: 'quantity',
                          align: 'center',
                        },
                        {
                          title: 'Đơn giá',
                          dataIndex: 'price',
                          key: 'price',
                          align: 'right',
                          render: (price) => formatCurrency(price),
                        },
                        {
                          title: 'Thành tiền',
                          dataIndex: 'amount',
                          key: 'amount',
                          align: 'right',
                          render: (amount) => formatCurrency(amount),
                        },
                      ]}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </Drawer>

      {/* Lịch sử trạng thái */}
      <Modal
        title="Lịch sử trạng thái hóa đơn"
        open={statusHistoryVisible}
        onCancel={() => setStatusHistoryVisible(false)}
        footer={null}
        width={600}
      >
        <Table
          dataSource={statusHistory}
          rowKey="id"
          pagination={false}
          size="small"
          columns={[
            {
              title: 'Thời gian',
              dataIndex: 'changed_at',
              key: 'changed_at',
              render: (date) => formatDate(date),
            },
            {
              title: 'Trạng thái cũ',
              dataIndex: 'old_status_id',
              key: 'old_status_id',
              render: (statusId) => getStatusInfo(statusId).name,
            },
            {
              title: 'Trạng thái mới',
              dataIndex: 'new_status_id',
              key: 'new_status_id',
              render: (statusId) => (
                <Tag color={getStatusInfo(statusId).color}>
                  {getStatusInfo(statusId).name}
                </Tag>
              ),
            },
            {
              title: 'Người thực hiện',
              dataIndex: 'changed_by_name',
              key: 'changed_by_name',
            },
            {
              title: 'Ghi chú',
              dataIndex: 'note',
              key: 'note',
            },
          ]}
        />
      </Modal>

      {/* Cập nhật trạng thái */}
      <Modal
        title="Cập nhật trạng thái hóa đơn"
        open={updateStatusVisible}
        onCancel={() => setUpdateStatusVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleStatusUpdate}
        >
          <Form.Item
            name="statusId"
            label="Trạng thái mới"
            rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}
          >
            <Select>
              {invoiceStatuses.map(status => {
                const statusInfo = getStatusInfo(status.id);
                return (
                  <Option key={status.id} value={status.id}>
                    <Tag color={statusInfo.color}>{status.name}</Tag>
                  </Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item
            name="note"
            label="Ghi chú"
            rules={[{ required: true, message: 'Vui lòng nhập ghi chú' }]}
          >
            <Input.TextArea rows={3} placeholder="Nhập ghi chú về việc cập nhật trạng thái" />
          </Form.Item>
          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setUpdateStatusVisible(false)}>
                Hủy
              </Button>
              <Button type="primary" htmlType="submit">
                Cập nhật
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InvoiceExportList;
